import React from 'react';
import { ACCENT, BG2, FONT, LIVE_GREEN, POST_GREY } from '../../lib/tokens';
import { Ic } from '../Icons';

const PRIMARY = [
  { id: 'home', label: 'Home', Icon: Ic.Home },
  { id: 'schedule', label: 'Schedule', Icon: Ic.Calendar },
  { id: 'parking-lot', label: 'Parking Lot', Icon: Ic.Lightbulb },
  { id: 'attendees', label: 'Attendees', Icon: Ic.People },
];

const SECONDARY = [
  { id: 'logistics', label: 'Logistics', Icon: Ic.Map },
  { id: 'commitments', label: 'Commitments', Icon: Ic.Check },
  { id: 'resources', label: 'Resources', Icon: Ic.Link },
  { id: 'uploads', label: 'Uploads & Links', Icon: Ic.Upload },
  { id: 'post-event', label: 'Post-Event', Icon: Ic.Shield },
];

export const SIDEBAR_WIDTH = 256;

export default function Sidebar({ activeScreen, onNavigate, isPostEvent }) {
  return (
    <nav
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        width: SIDEBAR_WIDTH,
        background: BG2,
        borderRight: '1px solid rgba(255,255,255,0.06)',
        display: 'flex',
        flexDirection: 'column',
        padding: '24px 14px 18px',
        zIndex: 20,
        fontFamily: FONT,
      }}
    >
      {/* Header: logo + LIVE/POST badge */}
      <div style={{ padding: '0 6px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <img
          src="/elx-logo-white.png"
          alt="ELX"
          style={{ height: 36, width: 'auto', objectFit: 'contain', alignSelf: 'flex-start' }}
        />
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            alignSelf: 'flex-start',
            background: isPostEvent ? 'rgba(148,163,184,0.12)' : 'rgba(34,197,94,0.14)',
            border: `1px solid ${isPostEvent ? 'rgba(148,163,184,0.25)' : 'rgba(34,197,94,0.35)'}`,
            borderRadius: 20,
            padding: '3px 10px',
          }}
        >
          <div
            style={{
              width: 5,
              height: 5,
              borderRadius: '50%',
              background: isPostEvent ? POST_GREY : LIVE_GREEN,
              boxShadow: isPostEvent ? 'none' : `0 0 5px ${LIVE_GREEN}`,
            }}
          />
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              color: isPostEvent ? POST_GREY : LIVE_GREEN,
              letterSpacing: '0.07em',
            }}
          >
            {isPostEvent ? 'POST-EVENT' : 'LIVE'}
          </span>
        </div>
      </div>

      {/* Primary nav */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {PRIMARY.map((item) => (
          <NavItem
            key={item.id}
            {...item}
            active={activeScreen === item.id}
            onClick={() => onNavigate(item.id)}
          />
        ))}
      </div>

      {/* Divider */}
      <div
        style={{
          height: 1,
          background: 'rgba(255,255,255,0.08)',
          margin: '14px 8px',
        }}
      />

      {/* Secondary nav */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {SECONDARY.map((item) => (
          <NavItem
            key={item.id}
            {...item}
            active={activeScreen === item.id}
            onClick={() => onNavigate(item.id)}
          />
        ))}
      </div>

      {/* Footer: for-the-curious link + faint admin link + hosted-by mark */}
      <div
        style={{
          marginTop: 'auto',
          padding: '12px 8px 0',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        <a
          href="/anatomy"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: 'rgba(255,255,255,0.5)',
            fontSize: 12.5,
            textDecoration: 'none',
            fontFamily: FONT,
            padding: '4px 0',
            letterSpacing: '0.02em',
          }}
        >
          For the curious →
        </a>
        <button
          onClick={() => onNavigate('admin')}
          style={{
            background: 'none',
            border: 'none',
            color: activeScreen === 'admin' ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.22)',
            fontSize: 12,
            cursor: 'pointer',
            fontFamily: FONT,
            padding: '4px 0',
            textAlign: 'left',
            letterSpacing: '0.04em',
          }}
        >
          ··· admin access
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: 0.4 }}>
          <span style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.6)', letterSpacing: '0.04em' }}>
            Hosted by
          </span>
          <img
            src="/chirocongress-logo-white.png"
            alt="ChiroCongress"
            style={{ height: 16, objectFit: 'contain' }}
          />
        </div>
      </div>
    </nav>
  );
}

function NavItem({ id, label, Icon, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 12px',
        background: active ? 'rgba(27,108,181,0.18)' : 'transparent',
        border: 'none',
        borderRadius: 9,
        cursor: 'pointer',
        textAlign: 'left',
        color: active ? '#fff' : 'rgba(255,255,255,0.7)',
        fontFamily: FONT,
        fontSize: 13.5,
        fontWeight: active ? 600 : 500,
        letterSpacing: '0.01em',
        transition: 'background 0.12s, color 0.12s',
      }}
      onMouseEnter={(e) => {
        if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = 'transparent';
      }}
    >
      <Icon size={18} color={active ? ACCENT : 'rgba(255,255,255,0.55)'} filled={active} />
      <span>{label}</span>
    </button>
  );
}

export { PRIMARY, SECONDARY };
